/**
 * Create Sandbox Group page component.
 *
 * @module pages/CreateSandboxGroupPage
 */

import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, Link } from 'react-router';
import { ArrowLeft, FolderPlus, Plug } from 'lucide-react';
import { PageHeader } from '@/components/layout';
import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Button,
  LabelsEditor,
  GeoRegionSelector,
  getRegionsFromGeoSelection,
} from '@/components/ui';
import type { LabelsEditorRef, GeoRegionKey } from '@/components/ui';
import { usePreviewMode } from '@/hooks';
import { useCreateSandboxGroup } from '@/hooks/useSandboxGroups';
import { connectionService } from '@/services/connectionService';
import type {
  SandboxGroupRequest,
  SandboxGroupResponse,
  ConnectionResponse,
  ConnectionTypeResponse,
} from '@/types';

interface FormState {
  labels: Record<string, string>;
  selectedConnections: string[];
  selectedGeoRegions: Set<GeoRegionKey>;
}

const initialFormState: FormState = {
  labels: {},
  selectedConnections: [],
  selectedGeoRegions: new Set(),
};

/**
 * Create sandbox group page
 */
export function CreateSandboxGroupPage() {
  const navigate = useNavigate();
  const createMutation = useCreateSandboxGroup();

  // Ref for labels editor to get uncommitted draft
  const labelsEditorRef = useRef<LabelsEditorRef>(null);

  // Connections state
  const [connections, setConnections] = useState<ConnectionResponse[]>([]);
  const [connectionTypes, setConnectionTypes] = useState<ConnectionTypeResponse[]>([]);
  const [connectionsLoading, setConnectionsLoading] = useState(true);
  const isPreviewMode = usePreviewMode();

  const [form, setForm] = useState<FormState>(initialFormState);

  // Fetch available connections and connection types (only Ready ones) - only in preview mode
  useEffect(() => {
    if (!isPreviewMode) {
      setConnectionsLoading(false);
      return;
    }
    const fetchConnections = async () => {
      try {
        const [allConnections, types] = await Promise.all([
          connectionService.list(),
          connectionService.listTypes(),
        ]);
        // Only show Ready connections
        setConnections(allConnections.filter((c) => c.state === 'Ready'));
        setConnectionTypes(types);
      } catch (err) {
        console.error('Failed to load connections:', err);
      } finally {
        setConnectionsLoading(false);
      }
    };
    fetchConnections();
  }, [isPreviewMode]);

  // Create a lookup map for connection type icons
  const typeIconMap = useMemo(() => {
    const map = new Map<string, string | undefined>();
    for (const type of connectionTypes) {
      map.set(type.name.toLowerCase(), type.iconUrl);
    }
    return map;
  }, [connectionTypes]);

  // Get the icon URL for a connection type
  const getIconUrl = (typeName: string): string | undefined => {
    return typeIconMap.get(typeName.toLowerCase());
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Include any uncommitted draft label from the labels editor
    const finalLabels = { ...form.labels };
    const draft = labelsEditorRef.current?.getDraft();
    if (draft) {
      finalLabels[draft.key] = draft.value;
    }

    // Convert geo regions to allowed locations
    const allowedLocations = getRegionsFromGeoSelection(form.selectedGeoRegions);

    const request: SandboxGroupRequest = {
      labels: Object.keys(finalLabels).length > 0 ? finalLabels : undefined,
      connections: form.selectedConnections.length > 0 ? form.selectedConnections : undefined,
      allowedLocations: allowedLocations.length > 0 ? allowedLocations : undefined,
    };

    createMutation.mutate(request, {
      onSuccess: (data: SandboxGroupResponse) => {
        navigate(`/sandbox-groups/${encodeURIComponent(data.id)}`);
      },
    });
  };

  const handleConnectionToggle = (connectionId: string) => {
    setForm((prev) => {
      const current = prev.selectedConnections;
      if (current.includes(connectionId)) {
        return { ...prev, selectedConnections: current.filter((id) => id !== connectionId) };
      } else {
        return { ...prev, selectedConnections: [...current, connectionId] };
      }
    });
  };

  return (
    <div>
      <PageHeader
        title="Create Sandbox Group"
        description="Create a new group to organize sandboxes with shared configuration"
        breadcrumbs={[{ label: 'Sandboxes', href: '/sandboxes' }, { label: 'Create Group' }]}
        action={
          <Link to="/sandboxes">
            <Button variant="secondary" leftIcon={<ArrowLeft className="h-4 w-4" />}>
              Cancel
            </Button>
          </Link>
        }
      />

      <form onSubmit={handleSubmit}>
        <div className="space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader
              title="Group Information"
              description="Provide details to identify this sandbox group"
            />
            <CardBody>
              <div className="space-y-4">
                <div>
                  <LabelsEditor
                    ref={labelsEditorRef}
                    value={form.labels}
                    onChange={(labels) => setForm((prev) => ({ ...prev, labels }))}
                    helperText="Add labels to identify this group (e.g., name: my-group, environment: dev)"
                    requireValue={false}
                  />
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Allowed Regions */}
          <Card>
            <CardHeader
              title="Geographic Regions"
              description="Restrict which regions sandboxes in this group can be created in"
            />
            <CardBody>
              <GeoRegionSelector
                value={form.selectedGeoRegions}
                onChange={(selectedGeoRegions) =>
                  setForm((prev) => ({ ...prev, selectedGeoRegions }))
                }
                helperText="Select one or more regions to restrict sandbox placement. Leave empty to allow all regions."
              />
            </CardBody>
          </Card>

          {/* Connectors - only in preview mode */}
          {isPreviewMode && (
            <Card>
              <CardHeader
                title="Connectors"
                description="Select connectors to be automatically applied to sandboxes in this group"
              />
              <CardBody>
                {connectionsLoading ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">Loading connectors...</p>
                ) : connections.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                      No connectors available
                    </p>
                    <Link to="/connectors">
                      <Button variant="secondary" size="sm">
                        Create Connector
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                      Sandboxes created in this group will automatically inherit these connectors
                      (unless they specify their own).
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {connections.map((conn) => {
                        const iconUrl = getIconUrl(conn.type);
                        return (
                          <label
                            key={conn.id}
                            className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                              form.selectedConnections.includes(conn.id)
                                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                                : 'border-gray-200 dark:border-[#2a2a2a] hover:border-gray-300 dark:hover:border-gray-600'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={form.selectedConnections.includes(conn.id)}
                              onChange={() => handleConnectionToggle(conn.id)}
                              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded-sm"
                            />
                            {/* Connector Icon */}
                            <div className="shrink-0 h-8 w-8 bg-gray-50 dark:bg-[#161616] rounded-lg flex items-center justify-center overflow-hidden">
                              {iconUrl ? (
                                <img
                                  src={iconUrl}
                                  alt={conn.type}
                                  className="h-5 w-5 object-contain"
                                  onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                    e.currentTarget.nextElementSibling?.classList.remove('hidden');
                                  }}
                                />
                              ) : null}
                              <Plug
                                className={`h-4 w-4 text-gray-400 ${iconUrl ? 'hidden' : ''}`}
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                {conn.name}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {conn.type}
                              </p>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                    {form.selectedConnections.length > 0 && (
                      <p className="text-sm text-primary-600 dark:text-primary-400 mt-3">
                        {form.selectedConnections.length} connector(s) selected
                      </p>
                    )}
                  </div>
                )}
              </CardBody>
            </Card>
          )}

          {/* Actions */}
          <Card>
            <CardFooter>
              <div className="flex justify-end gap-3">
                <Link to="/sandboxes">
                  <Button variant="secondary">Cancel</Button>
                </Link>
                <Button
                  type="submit"
                  leftIcon={<FolderPlus className="h-4 w-4" />}
                  isLoading={createMutation.isPending}
                >
                  Create Group
                </Button>
              </div>
            </CardFooter>
          </Card>
        </div>
      </form>
    </div>
  );
}
